import React from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import s from './BecomeAPro.css';
import qr from './qrDataUri';

// Real store listings for the BestHand Pro (worker) app.
const APP_STORE = 'https://apps.apple.com/us/app/besthand-pro/id6759719595';
const PLAY_STORE =
  'https://play.google.com/store/apps/details?id=com.worldtechnologiessolutionsllc.besthandpro&hl=en_US';

const Icon = ({ d }) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d={d} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BENEFITS = [
  {
    d: 'M3 21v-2a4 4 0 0 1 4-4h4m6-1 2 2 4-4M9 7a3 3 0 1 0 6 0 3 3 0 0 0-6 0Z',
    title: 'More clients, no cold calls',
    text: 'We send paying jobs straight to your phone from customers nearby. You bring the skills — we bring the work.',
  },
  {
    d: 'M12 7v5l3 2m6-2a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z',
    title: 'Work on your own schedule',
    text: 'Go online whenever you want. Pick the jobs that fit your day — no shifts, no quotas, no boss.',
  },
  {
    d: 'M12 1v22m5-18H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
    title: 'Get paid the same day',
    text: 'Cash out the day you finish — right after the job is done, as soon as you meet the payout conditions.',
  },
  {
    d: 'M5 13l4 4L19 7',
    title: "You're always in control",
    text: 'Free to accept or decline any request. You choose the services you offer and the areas you cover.',
  },
  {
    d: 'M3 17l6-6 4 4 8-8m0 0h-5m5 0v5',
    title: 'Grow your business',
    text: 'Earn reviews, climb performance tiers (Bronze to Platinum), and unlock more — and bigger — jobs over time.',
  },
  {
    d: 'M12 8c-1.7 0-3 .9-3 2s1.3 2 3 2 3 .9 3 2-1.3 2-3 2m0-8V6m0 12v-2M5 12a7 7 0 1 0 14 0 7 7 0 0 0-14 0Z',
    title: 'Free to join, fair fees',
    text: 'Download free and start earning today. Transparent, percentage-based fees only — never hidden charges.',
  },
];

const STEPS = [
  ['Download & sign up', 'Create your BestHand Pro account in minutes.'],
  // COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod.
  ['Get verified', 'Complete a quick verification step and add your services.'],
  ['Start earning', 'Accept nearby jobs, do great work, and get paid fast.'],
];

const AppleBadge = () => (
  <a className={s.badge} href={APP_STORE} target="_blank" rel="noopener noreferrer">
    <svg viewBox="0 0 24 24" className={s.badgeLogo} fill="currentColor" aria-hidden="true">
      <path d="M16.4 12.8c0-2.2 1.8-3.3 1.9-3.4-1-1.5-2.6-1.7-3.2-1.7-1.4-.1-2.7.8-3.3.8-.7 0-1.7-.8-2.8-.8-1.5 0-2.8.8-3.6 2.1-1.5 2.7-.4 6.6 1.1 8.8.7 1 1.6 2.2 2.7 2.2 1.1 0 1.5-.7 2.8-.7s1.6.7 2.8.7c1.1 0 1.9-1 2.6-2.1.8-1.2 1.2-2.4 1.2-2.5-.1 0-2.2-.9-2.2-3.4Zm-2.2-6.2c.6-.7 1-1.7.9-2.7-.9 0-1.9.6-2.5 1.3-.5.6-1 1.6-.9 2.6 1 .1 1.9-.5 2.5-1.2Z" />
    </svg>
    <span className={s.badgeTxt}>
      <small>Download on the</small>
      <strong>App Store</strong>
    </span>
  </a>
);

const PlayBadge = () => (
  <a className={s.badge} href={PLAY_STORE} target="_blank" rel="noopener noreferrer">
    <svg viewBox="0 0 24 24" className={s.badgeLogo} aria-hidden="true">
      <path fill="#34A853" d="m13.5 12 5.8-3.3-12.7-7c-.3-.2-.7-.3-1-.2L13.5 12Z" />
      <path fill="#4285F4" d="M4.6 1.5c-.3.3-.5.7-.5 1.2v18.6c0 .5.2.9.5 1.2l9-10.5-9-10.5Z" />
      <path fill="#FBBC04" d="m13.5 12 3.6 3.6 4.3-2.4c.6-.4.6-1.2 0-1.6l-4.3-2.4L13.5 12Z" />
      <path fill="#EA4335" d="m4.6 22.5 8.9-10.5 3.6 3.6-11.5 7c-.3.2-.7.1-1-.1Z" />
    </svg>
    <span className={s.badgeTxt}>
      <small>GET IT ON</small>
      <strong>Google Play</strong>
    </span>
  </a>
);

const BecomeAPro = () => {
  const [os, setOs] = React.useState('desktop');
  React.useEffect(() => {
    const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
    if (/iPhone|iPad|iPod/i.test(ua)) setOs('ios');
    else if (/Android/i.test(ua)) setOs('android');
    else setOs('desktop');
  }, []);

  const badges =
    os === 'android' ? (
      <><PlayBadge /><AppleBadge /></>
    ) : (
      <><AppleBadge /><PlayBadge /></>
    );

  return (
    <div className={s.page}>
      {/* HERO */}
      <section className={s.hero}>
        <div className={s.wrap}>
          <span className={s.eyebrow}>BestHand Pro · for workers &amp; companies</span>
          <h1 className={s.h1}>
            Turn your skills into <span className={s.hl}>steady income.</span>
          </h1>
          <p className={s.sub}>
            Join thousands of pros on BestHand. Get more clients, work on your own terms, and get paid
            fast — all from one app.
          </p>

          <div className={s.dl}>
            <div className={s.badges}>{badges}</div>
            {os === 'desktop' && (
              <div className={s.qrCard}>
                <img className={s.qr} src={qr} alt="Scan to download the BestHand Pro app" />
                <span>Scan with your phone<br />to download the app</span>
              </div>
            )}
          </div>
          <p className={s.note}>Free to download · iOS &amp; Android</p>
        </div>
      </section>

      {/* TWO PATHS — individual worker vs company */}
      <section className={s.sec}>
        <div className={s.wrap}>
          <h2 className={s.h2}>Two ways to earn with BestHand</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 18, marginTop: 20 }}>
            {/* Worker */}
            <div style={{ border: '1px solid #ECEEF1', borderRadius: 18, padding: 26, borderTop: '4px solid #E8232B', background: '#fff' }}>
              <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: '#E8232B' }}>Individual pro</div>
              <h3 style={{ fontSize: 23, fontWeight: 800, margin: '6px 0 10px', color: '#1F2733' }}>Join as a worker</h3>
              <p style={{ color: '#5B6472', lineHeight: 1.55, margin: '0 0 14px' }}>For solo pros &amp; handymen. Pick up small jobs near you, set your own schedule, keep your hourly rate, and get paid fast — straight from the app. Free to join, no lead fees.</p>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 18px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {['Free to join — keep your rate, no lead fees', 'Work whenever you want — accept the jobs you like', 'Get paid fast, securely through the app', 'Background-check support builds client trust'].map((x) => (
                  <li key={x} style={{ position: 'relative', paddingLeft: 24, color: '#374151', fontWeight: 600, fontSize: 14 }}><span style={{ position: 'absolute', left: 0, color: '#0A7C70', fontWeight: 900 }}>✓</span>{x}</li>
                ))}
              </ul>
              <div className={s.badges}>{badges}</div>
            </div>
            {/* Company */}
            <div style={{ border: '1px solid #ECEEF1', borderRadius: 18, padding: 26, borderTop: '4px solid #0A7C70', background: '#fff' }}>
              <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: '#0A7C70' }}>Company</div>
              <h3 style={{ fontSize: 23, fontWeight: 800, margin: '6px 0 10px', color: '#1F2733' }}>Join as a company</h3>
              <p style={{ color: '#5B6472', lineHeight: 1.55, margin: '0 0 14px' }}>For businesses that take on bigger jobs. Get matched to Large Projects, send competing quotes, and get paid by milestone through secure escrow. Verified company profile — client identities stay masked until you&apos;re hired.</p>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 18px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {['Quote large home-improvement & construction projects', 'Verified, licensed & insured company profile', 'Milestone payments held in escrow — paid as work is approved', 'Dedicated company dashboard, no client-poaching'].map((x) => (
                  <li key={x} style={{ position: 'relative', paddingLeft: 24, color: '#374151', fontWeight: 600, fontSize: 14 }}><span style={{ position: 'absolute', left: 0, color: '#0A7C70', fontWeight: 900 }}>✓</span>{x}</li>
                ))}
              </ul>
              <a href="/become-a-company" style={{ display: 'inline-block', background: '#0A7C70', color: '#fff', fontWeight: 800, fontSize: 15, padding: '13px 24px', borderRadius: 12, textDecoration: 'none' }}>Join as a company →</a>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className={s.sec}>
        <div className={s.wrap}>
          <h2 className={s.h2}>Why pros choose BestHand</h2>
          <div className={s.grid}>
            {BENEFITS.map((b) => (
              <div className={s.card} key={b.title}>
                <div className={s.cardIc}><Icon d={b.d} /></div>
                <h3>{b.title}</h3>
                <p>{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className={`${s.sec} ${s.steps}`}>
        <div className={s.wrap}>
          <h2 className={s.h2}>Start earning in 3 steps</h2>
          <div className={s.stepRow}>
            {STEPS.map(([t, d], i) => (
              <div className={s.step} key={t}>
                <div className={s.stepNo}>{i + 1}</div>
                <h3>{t}</h3>
                <p>{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className={s.finalCta}>
        <div className={s.wrap}>
          <h2 className={s.h2White}>Ready to grow your business?</h2>
          <p className={s.subWhite}>Download BestHand Pro and take your first job today.</p>
          <div className={s.badges}>{badges}</div>
        </div>
      </section>
    </div>
  );
};

export default withStyles(s)(BecomeAPro);
